'use client';

/**
 * The free try has a date on it.
 *
 * The starter grant lives two weeks and used to say nothing: a parent who came
 * back a month later learned about it when an order refused to start
 * (`FIRST.R20`, gap 7). So the term is said twice — once where the order is
 * confirmed (`inline`), and once as a reminder while it is still spendable
 * (`banner`, from five days out).
 *
 * Only for families with no subscription and a live grant that expires: a
 * subscriber's allowance refills instead of burning, and `AllowanceMeter`
 * already says when.
 */

import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Clock } from 'lucide-react';
import { useAllowanceMeter } from '../hooks/useAllowanceMeter';
import { openSubscription } from '../lib/accountsTopUp';

/** From this many days out the reminder becomes a banner. */
const REMIND_WITHIN_DAYS = 5;
const DISMISS_KEY = 'owlcat.freeTry.dismissedOn';

function daysLeft(iso: string): number | null {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.ceil((then - Date.now()) / 86_400_000);
}

export function FreeTryNote({ variant = 'inline' }: { variant?: 'inline' | 'banner' }) {
  const t = useTranslations('subscription.freeTry');
  const locale = useLocale();
  const { data } = useAllowanceMeter();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const on = localStorage.getItem(DISMISS_KEY);
      if (on === new Date().toISOString().slice(0, 10)) setDismissed(true);
    } catch { /* private mode — the reminder simply shows */ }
  }, []);

  if (!data || data.hasSubscription || !data.hasAllowance || !data.nextExpiry) return null;
  const left = daysLeft(data.nextExpiry);
  if (left == null || left < 0) return null;

  const when = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' })
    .format(new Date(data.nextExpiry));

  if (variant === 'inline') {
    return (
      <p className="text-xs text-muted-foreground" data-testid="free-try-inline">
        {t('inline', { when, days: left })}
      </p>
    );
  }

  if (dismissed || left > REMIND_WITHIN_DAYS) return null;

  return (
    <div
      className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-x-3 gap-y-1 border-b border-warning/30 bg-warning/10 px-4 py-2 text-sm"
      data-testid="free-try-banner"
    >
      <Clock className="size-4 shrink-0 text-warning-foreground dark:text-warning" />
      <span>{t('banner', { days: left, when })}</span>
      <button
        type="button"
        onClick={() => openSubscription(locale)}
        className="font-medium text-primary hover:underline"
        data-testid="free-try-subscribe"
      >
        {t('subscribe')}
      </button>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          try { localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10)); } catch {}
        }}
        className="ml-auto text-muted-foreground hover:text-foreground"
        data-testid="free-try-dismiss"
      >
        {t('hide')}
      </button>
    </div>
  );
}
